import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { SxProps, Theme } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { AccountStage } from '@provider-portal/common/services/types/account';
import { Box } from '@provider-portal/design-common/kalep/components';
import { BoltThemeProvider } from '@provider-portal/design-common/kalep/theme/BoltThemeProvider';
import { TabHeader } from 'components/merchant-portal-2/TabHeader/TabHeader';
import { PromotionsEntry } from 'PromotionsEntry';
import { CustomerSupport } from 'widgets/CustomerSupport/CustomerSupport';
import { HandHoldAgent } from 'widgets/HandHoldAgent/HandHoldAgent';
import { OnboardingProviderSupport } from 'widgets/OnboardingProvidersSupport/OnboardingProviderSupport';

import { selectAccountStage } from 'rdx/account';

import { ActiveTabHeader } from 'root/navigation/navStructureUtils';
import { usePathAwareDocumentTitle } from 'root/navigation/usePathAwareDocumentTitle';
import { ReturnToOnboardingLink } from 'root/components/ReturnToOnboardingLink/ReturnToOnboardingLink';
import { useHasPermanentNavBar } from 'components/merchant-portal-2/NavBar/useHasPermanentNavBar';

// Extend Window interface for Microsoft Clarity
declare global {
  interface Window {
    clarity?: (action: string, key: string, value: string) => void;
  }
}

interface MainContentProps {
  children: React.ReactNode;
  activeTabHeader: ActiveTabHeader | null;
}

export function MainContent({ children, activeTabHeader }: MainContentProps) {
  usePathAwareDocumentTitle();
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const intl = useIntl();
  const accountStage = useSelector(selectAccountStage);

  const setRef = useCallback((ref) => {
    setContainerRef(ref);
  }, []);

  useEffect(() => {
    if (containerRef) {
      const scrollContainer = containerRef as HTMLElement;
      scrollContainer.scrollTop = 0;
    }
  }, [containerRef, location.pathname]);

  const isAccountActivated = useMemo(() => accountStage === AccountStage.ACTIVATED, [accountStage]);

  const hasPermanentNavBar = useHasPermanentNavBar();
  const mainContentMargin = hasPermanentNavBar ? '228px' : 0;

  const gridStyle: SxProps<Theme> = {
    ml: mainContentMargin,
    px: { xs: '16px', md: '32px' },
    pt: { xs: '12px', md: '24px' },
    pb: { xs: '12px', md: '32px' },
  };

  if (window.clarity && process.env.REACT_APP_STAGE === 'live') {
    window.clarity('set', 'current_ui_version', 'newVersion');
  }

  return (
    <BoltThemeProvider>
      <main data-testid="home-screen" ref={setRef} className="mainContent">
        <Grid sx={gridStyle}>
          <ReturnToOnboardingLink />
          {activeTabHeader && <TabHeader {...activeTabHeader} />}
          <Grid item xs={12} className="pageContent">
            <Box>
              {isAccountActivated && (
                <Box>
                  <PromotionsEntry />
                </Box>
              )}
              {children}
            </Box>
          </Grid>
        </Grid>
        {isAccountActivated ? <CustomerSupport /> : <OnboardingProviderSupport />}
        {isAccountActivated && <HandHoldAgent locale={intl.locale} />}
      </main>
    </BoltThemeProvider>
  );
}
